import React, {useState} from "react";
import {Tooltip, Tag, Modal, Select} from "antd";
import {useObserver} from "mobx-react-lite";
import {useStore} from "../../../hooks/useStore";
import moment from "moment";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {CourierLink} from "../../_defaultComponents/CourierLink/CourierLink";
import {
    ClockCircleOutlined,
    UserSwitchOutlined,
    DeleteOutlined,
    HistoryOutlined
} from "@ant-design/icons";
import {ActionButtons, TableContainer} from "./ComponentStyles";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {
    TimeChangeModal,
    CourierChangeModal,
    timeChangeReasons,
    courierChangeReasons
} from "./PoolShiftChangeModals";
import {useFetch} from "../../../hooks/useFetch";

const allChangeReasons = new Map(
    [...timeChangeReasons, ...courierChangeReasons].map(reason => [reason.value, reason.label])
);

const PoolShiftTable = ({loading, data, dataCount, currentPage, setCurrentPage, setReloadData}) => {
    const stores = useStore();
    const [timeModalVisible, setTimeModalVisible] = useState(false);
    const [courierModalVisible, setCourierModalVisible] = useState(false);
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const [changeHistoryModalVisible, setChangeHistoryModalVisible] = useState(false);
    const [selectedCourier, setSelectedCourier] = useState(null);
    const [selectedShift, setSelectedShift] = useState(null);
    const [cancelNote, setCancelNote] = useState(null);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [changeHistory, setChangeHistory] = useState([]);

    const [shiftHoursLoading, shiftHours, setShiftHours] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.poolShiftStore.getShiftHours.bind(null, {}),
        dependencies: [],
        initialCall: true
    });

    const handleTimeChange = record => {
        setShiftData(record);
        setTimeModalVisible(true);
    };

    const handleCourierChange = record => {
        setShiftData(record);
        setCourierModalVisible(true);
    };

    const handleDelete = record => {
        setRecordToDelete(record);
        setCancelModalVisible(true);
    };

    const handleChangeHistory = async record => {
        setChangeHistoryModalVisible(true);
        console.log(record.changedIds);
        const poolShiftSyChangesList = await stores.poolShiftStore.getPoolShiftSyChangeLogs({
            poolShiftSyChangeLogIds: record.changedIds
        });
        console.log(poolShiftSyChangesList);
        setChangeHistory(poolShiftSyChangesList.data);
    };

    const handleConfirmDelete = async () => {
        if (recordToDelete && cancelNote) {
            await stores.poolShiftStore.cancelCourierPoolShift({
                courierId: recordToDelete.courier._id,
                poolId: recordToDelete.shiftId,
                date: recordToDelete.date,
                reason: cancelNote,
                startHourId: recordToDelete.startHourId,
                endHourId: recordToDelete.endHourId
            });
            setCancelModalVisible(false);
            setReloadData(prev => !prev);
            setCancelNote(null);
            setRecordToDelete(null);
        }
    };

    const handleCancelModal = () => {
        setCancelModalVisible(false);
        setCancelNote(null);
        setRecordToDelete(null);
    };

    const setShiftData = record => {
        setSelectedCourier(record.courier);
        setSelectedShift({
            shiftDate: record.date,
            shiftName: record.shiftName,
            poolId: record.shiftId,
            plannedTime: record.plannedTime,
            startHourId: record.startHourId,
            endHourId: record.endHourId,
            plannedStartTime: record.plannedStartTime,
            plannedEndTime: record.plannedEndTime
        });
    };

    const cancellationReasons = [
        {value: "kaza", label: "Kaza"},
        {value: "uyumsuzluk", label: "Uyumsuzluk"},
        {value: "hatali_vardiya", label: "Hatalı Vardiya"},
        {value: "saglik", label: "Sağlık"},
        {value: "erken_giris", label: "Erken Giriş"}
    ];

    const columns = [
        {
            title: "Vardiya Adı",
            dataIndex: "shiftName",
            key: "shiftName"
        },
        {
            title: "Kurye Adı",
            dataIndex: "courier",
            key: "courier",
            render: courier => (
                <span data-testid="date">
                    <CourierLink target="_blank" to={`/couriers/${courier?._id}`}>
                        {courier?.name}
                    </CourierLink>
                </span>
            )
        },
        {
            title: "Planlanan Saat",
            dataIndex: "plannedTime",
            key: "plannedTime"
        },
        {
            title: "Gerçekleşen Saat",
            dataIndex: "actualTime",
            key: "actualTime"
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            render: status => {
                const colors = {
                    waiting: "#faad14",
                    active_courier_entered: "#52c41a",
                    active_courier_not_entered: "#ff4d4f",
                    completed_courier_entered: "#389e0d",
                    completed_courier_not_entered: "#cf1322",
                    cancelled: "#cf1322"
                };

                const statuses = {
                    waiting: "Beklemede",
                    active_courier_entered: "Aktif (Giriş Yapıldı)",
                    active_courier_not_entered: "Aktif (Giriş Yapılmadı)",
                    completed_courier_entered: "Tamamlandı (Giriş Yapıldı)",
                    completed_courier_not_entered: "Tamamlandı (Giriş Yapılmadı)",
                    cancelled: "İptal Edildi"
                };

                return <Tag color={colors[status]}>{statuses[status]}</Tag>;
            }
        },
        {
            title: "Planlanan Çalışma",
            dataIndex: "plannedWorkingHours",
            key: "plannedWorkingHours"
        },
        {
            title: "Çalışma Saati",
            dataIndex: "workingHours",
            key: "workingHours"
        },
        {
            title: "Değişiklik Sebebi",
            dataIndex: "cancelNote",
            key: "cancelNote",
            render: cancelNote => {
                return allChangeReasons.get(cancelNote) || cancelNote;
            }
        },
        {
            title: "Aksiyon",
            key: "action",
            render: (_, record) => {
                const startTime = moment(
                    record.date + " " + record.plannedStartTime,
                    "YYYY-MM-DD HH:mm"
                );
                let endTime = moment(record.date + " " + record.plannedEndTime, "YYYY-MM-DD HH:mm");
                if (endTime.isBefore(startTime)) {
                    endTime.add(1, "day");
                }
                const updateVisibility = endTime >= moment();
                const deleteVisibility =
                    (stores.meStore.checkRole("isAdmin") ||
                        stores.meStore.checkRole("isOperationUser")) &&
                    !record.cancelNote;

                const isBeforeCurrentTime = endTime.isBefore(moment());

                const changeHistoryVisibility = record.cancelNote;
                return (
                    <ActionButtons>
                        <Tooltip title={"Saat Değiştir"}>
                            <CustomButton
                                data-testid="changeTimeButton"
                                onClick={() => handleTimeChange(record)}
                                icon={<ClockCircleOutlined />}
                                disabled={record.cancelNote || isBeforeCurrentTime}
                            />
                        </Tooltip>

                        <Tooltip title={"Kurye Değiştir"}>
                            <CustomButton
                                data-testid="changeCourierButton"
                                onClick={() => handleCourierChange(record)}
                                icon={<UserSwitchOutlined />}
                                disabled={record.cancelNote || isBeforeCurrentTime}
                            />
                        </Tooltip>

                        {deleteVisibility && (
                            <Tooltip title={"Sil"}>
                                <CustomButton
                                    data-testid="courierShiftDeleteButton"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                        handleDelete(record);
                                    }}
                                    disabled={record.cancelNote || isBeforeCurrentTime}
                                />
                            </Tooltip>
                        )}

                        {changeHistoryVisibility && (
                            <Tooltip title={"Değişim Geçmişi"}>
                                <CustomButton
                                    data-testid="courierShiftHistoryButton"
                                    icon={<HistoryOutlined />}
                                    onClick={() => {
                                        handleChangeHistory(record);
                                    }}
                                />
                            </Tooltip>
                        )}
                    </ActionButtons>
                );
            }
        }
    ];

    const changeHistoryColumns = [
        {
            title: "Güncelleme Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: createdAt => {
                return moment(createdAt).format("DD.MM.YYYY HH:mm");
            }
        },
        {
            title: "Aksiyon",
            dataIndex: "action",
            key: "action",
            render: (_, record) => {
                const actions = {
                    shift_added: "Vardiya Eklendi",
                    time_change: "Saat Değiştirildi",
                    courier_change: "Kurye Değiştirildi",
                    shift_canceled: "Vardiya İptal Edildi"
                };
                return actions[record.action];
            }
        },
        {
            title: "Eski Değer",
            dataIndex: "oldValue",
            key: "oldValue"
        },
        {
            title: "Yeni Değer",
            dataIndex: "newValue",
            key: "newValue"
        },
        {
            title: "Değişim Sebebi",
            dataIndex: "reason",
            key: "reason",
            render: reason => {
                return allChangeReasons.get(reason) || reason;
            }
        },
        {
            title: "Güncelleme Yapan Kişi",
            dataIndex: "createdBy",
            key: "createdBy"
        }
    ];

    return useObserver(() => {
        return (
            <>
                <TableContainer
                    loading={loading}
                    columns={columns}
                    dataSource={data}
                    pagination={{
                        current: currentPage,
                        total: dataCount,
                        pageSize: 10,
                        onChange: page => {
                            setCurrentPage(page);
                        }
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                    display={data.length > 0 ? "grid" : "flex"}
                    scroll={{x: "max-content"}}
                    rowKey="_id"
                    dataTitles={[
                        "Vardiya Adı",
                        "Kurye Adı",
                        "Planlanan Saat",
                        "Gerçekleşen Saat",
                        "Durum",
                        "Planlanan Çalışma",
                        "Çalışma Saati",
                        "Değişiklik Sebebi",
                        "Aksiyon"
                    ]}
                ></TableContainer>

                <TimeChangeModal
                    visible={timeModalVisible}
                    onCancel={() => setTimeModalVisible(false)}
                    courier={selectedCourier}
                    shiftData={selectedShift}
                    shiftHours={shiftHours?.data || []}
                    onSuccess={() => {
                        setReloadData(prev => !prev);
                    }}
                />

                <CourierChangeModal
                    visible={courierModalVisible}
                    onCancel={() => setCourierModalVisible(false)}
                    courier={selectedCourier}
                    shiftData={selectedShift}
                    shiftHours={shiftHours?.data || []}
                    onSuccess={() => {
                        setReloadData(prev => !prev);
                    }}
                />

                <Modal
                    title="Vardiya İptali"
                    visible={cancelModalVisible}
                    onOk={handleConfirmDelete}
                    onCancel={handleCancelModal}
                    okText="İptal Et"
                    cancelText="Vazgeç"
                    okButtonProps={{disabled: !cancelNote}}
                >
                    <p>Bu vardiyayı iptal etmek istediğinizden emin misiniz?</p>
                    <Select
                        style={{width: "100%", marginTop: "16px"}}
                        placeholder="İptal sebebini seçin"
                        value={cancelNote}
                        onChange={setCancelNote}
                        options={cancellationReasons}
                    />
                </Modal>

                <Modal
                    title="Değişim Geçmişi"
                    visible={changeHistoryModalVisible}
                    onCancel={() => setChangeHistoryModalVisible(false)}
                    cancelText="Kapat"
                    okButtonProps={{style: {display: "none"}}}
                    width={1000}
                >
                    <TableContainer
                        dataSource={changeHistory}
                        columns={changeHistoryColumns}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                        pagination={false}
                        display={changeHistory.length > 0 ? "grid" : "flex"}
                        scroll={{x: "max-content"}}
                        rowKey="_id"
                        dataTitles={[
                            "Güncelleme Tarihi",
                            "Aksiyon",
                            "Eski Değer",
                            "Yeni Değer",
                            "Değişim Sebebi",
                            "Güncelleme Yapan Kişi"
                        ]}
                    />
                </Modal>
            </>
        );
    });
};

export default PoolShiftTable;
